import { KTSVG } from '_metronic/helpers';
import NumericComponent from 'app/components/FormComponents/NumericComponent';
import SelectComponent from 'app/components/FormComponents/SelectComponent';
import * as Yup from 'yup';
import { getPerceptionDeductions } from 'app/service/catalog/Catalogs';
import { SelectOption } from 'app/types/commons';
import { useFormik } from 'formik';
import React, { useEffect, useMemo, useState } from 'react';
import { Modal } from 'react-bootstrap-v5';
import { CollaboratorModelNET } from '../../models/CollaboratorModelNET';
import { toast } from 'react-toastify';
import { PerceptionDeduction } from 'app/types/perceptions';
import { getCollaboratorDeductions, postDeductions, putDeductions } from 'app/service/deductions/deductions';
import { Deduction } from 'app/types/deductions';
import TableComponent from 'app/components/TableComponent';
import { Collaborator } from 'app/types/collaborator';

type Props = {
    collaborator?: Collaborator;
};

type DeductionOption = SelectOption & {
    typeId?: number;
};

type FormValues = {
    typeDeduction: DeductionOption | null;
    amount?: number;
};

const initialValues: FormValues = {
    typeDeduction: null,
    amount: 0,
};

const prevHeads = [
    {
        label: 'Concepto',
        code: 'deduction',
    },
    {
        label: 'Valor',
        code: 'value',
    },
    {
        label: 'Acciones',
        code: 'acciones',
    },
];

const validationSchema = Yup.object({
    typeDeduction: Yup.object({
        value: Yup.number(),
        label: Yup.string(),
    })
        .nullable()
        .required('Este campo es requerido'),
    amount: Yup.number()
        .when('typeDeduction', (typeDeduction, schema) => {
            if ([3, 4].includes(Number(typeDeduction?.typeId))) {
                return schema
                    .required('Este campo es requerido')
                    .min(0.1, "El valor debe ser mayor que 0")
                    .max(999999, 'El valor total no puede ser mayor a 999,999');
            }
            return schema.notRequired().nullable();
        }),
});

const Deductions: React.FC<Props> = ({ collaborator }) => {
    const [show, setShow] = useState(false);
    const [typesDeduction, setTypesDeduction] = useState<PerceptionDeduction[]>([]);
    const [selectDeduction, setSelectDeduction] = useState<DeductionOption[]>([]);
    const [prevRows, setPrevRows] = useState<any[]>([]);
    const [loading, setLoading] = useState(false);
    const formData = useFormik<FormValues>({
        initialValues,
        validationSchema,
        onSubmit: async (values, { setSubmitting }) => {
            setLoading(true);
            try {
                await postDeductions({
                    collaboratorId: Number(collaborator!.id),
                    deductionId: values.typeDeduction?.value,
                    value: Number(value),
                })
                formData.resetForm();
                handleClose();
                fillDataAsync();
                const message = 'Se actualizó la información de deducciones con éxito';
                toast.success(message, {
                    position: 'top-right',
                    autoClose: 3000,
                    hideProgressBar: false,
                    closeOnClick: true,
                    pauseOnHover: true,
                    draggable: true,
                    progress: undefined,
                    theme: 'light',
                });
            } catch (error: any) {
                console.error('error en submit', error)
                const defaultError = 'Hubo un error al intentar actualizar los datos de deducciones';
                toast.error(error.message ?? defaultError, {
                    position: 'top-right',
                    autoClose: 4000,
                    hideProgressBar: false,
                    closeOnClick: true,
                    pauseOnHover: true,
                    draggable: true,
                    progress: undefined,
                    theme: 'light',
                });
            } finally {
                setLoading(false);
                setSubmitting(false);
            }
        },
    });
    const typeDeduction = useMemo(() => {
        if (formData.values.typeDeduction?.value && typesDeduction) {
            return typesDeduction.find((item) => item.id === formData.values.typeDeduction?.value);
        }
        return null;
    }, [formData.values.typeDeduction, typesDeduction]);
    const value = useMemo(() => {
        if (typeDeduction) {
            switch (typeDeduction.typePerceptionDeductionId) {
                case 1:
                    return typeDeduction.value;
                case 2:
                    switch (typeDeduction.factorBaseId) {
                        case 1:
                            return typeDeduction.value * Number(collaborator!.employe?.baseSalary);
                        case 2:
                            return typeDeduction.value * Number(collaborator!.employe?.dayliSalary);
                        case 3:
                            return typeDeduction.value * Number(collaborator!.employe?.baseSalary) / collaborator!.employe?.factor[0].value!;
                        default:
                            return typeDeduction.value * Number(collaborator!.employe?.baseSalary);
                    }
                case 3:
                    return (formData.values.amount ?? 0) * Number(typeDeduction.value);
                case 4:
                default:
                    return (Number(formData.values.amount ?? 0));
            }
        }
        return 0;
    }, [typeDeduction, formData.values.amount]);
    const handleClose = () => {
        formData.resetForm();
        setShow(false);
    };
    const addRow = () => {
        setShow(true);
    };
    const handleDeleteDeduction = async (item: Deduction) => {
        try {
            await putDeductions(item.id!, {
                id: item.id!,
                deductionId: item.deductionId,
                collaboratorId: item.collaboratorId,
                value: item.value,
                statusId: 2
            });
            toast.success('Se eliminó la deducción con éxito');
            fillDataAsync();
        } catch (error: any) {
            console.error('error deleting deduction', error);
            toast.error(error.message ?? 'Hubo un error al intentar eliminar la deducción');
        }
    };
    const fillDataAsync = async () => {
        try {
            const typesRes = await getPerceptionDeductions();
            const prevRes = await getCollaboratorDeductions(collaborator!.id!)
                .catch((error) => {
                    console.error('error al cargar las deducciones anteriores', error);
                });
            if (prevRes) setPrevRows(prevRes.filter((item) => item.statusId !== 2).map((item) => ({
                deduction: item.deduction,
                value: item.value.toLocaleString(),
                acciones: (
                    <div
                        style={{
                            textAlign: 'center',
                            cursor: 'pointer',
                        }}
                        onClick={() => handleDeleteDeduction(item)}
                    >
                        <KTSVG
                            path="/media/icons/duotune/general/gen027.svg"
                            className="svg-icon-3 svg-icon-danger"
                        />
                    </div>
                )
            })));
            setTypesDeduction(typesRes.records);
            setSelectDeduction(typesRes.records
                .filter((item) => item.typeMovementId === 2)
                .map((item) => ({
                    value: item.id,
                    label: item.description,
                    typeId: item.typePerceptionDeductionId,
                })));
        } catch (error) {
            console.error('error en carga de catalogos', error)
        }
    };
    useEffect(() => {
        if (collaborator)
            fillDataAsync();
    }, [collaborator]);
    return (
        <div>
            <div>
                <h2>Frecuencia de pago:</h2>
                <div className='d-flex align-items-center gap-2'>
                    <div className='w-10 me-2'>
                        <input
                            className='form-control'
                            type='text'
                            value="Quincenal"
                            disabled
                        />
                    </div>
                    <h1>15</h1>
                    <h3>días</h3>
                </div>
            </div>
            <div className='w-100 d-flex justify-content-center'>
                <div className='w-50'>
                    <TableComponent heads={prevHeads} rows={prevRows} />
                </div>
            </div>
            <div className="d-flex align-items-center mt-4">
                <button
                    className="btn btn-light-primary btn-sm mb-10"
                    onClick={addRow}
                    disabled={!collaborator?.employe}
                >
                    <KTSVG
                        path="/media/icons/duotune/arrows/arr075.svg"
                        className="svg-icon-5"
                    />
                    Agregar Deducción
                </button>
            </div>
            <Modal
                id="kt_modal_create"
                tabIndex={-1}
                aria-hidden="true"
                dialogClassName="modal-dialog"
                show={show}
                onHide={handleClose}
            >
                <div className="modal-content">
                    <div className="modal-header">
                        <h2 className="text-muted">Deducciones</h2>
                        <div
                            className="btn btn-sm btn-icon btn-active-color-primary"
                            onClick={handleClose}
                        >
                            <KTSVG
                                className="svg-icon-2"
                                path="/media/icons/duotune/arrows/arr061.svg"
                            />
                        </div>
                    </div>
                    <div className="modal-body mx-5 padding-top-00">
                        <form
                            id="kt_modal_generic_form"
                            className="form"
                            action="#"
                            onSubmit={formData.handleSubmit}
                        >
                            <div className='m-3'>
                                <SelectComponent
                                    options={selectDeduction}
                                    formData={formData}
                                    name='typeDeduction'
                                    display={'-1'}
                                />
                                {formData.touched.typeDeduction && formData.errors.typeDeduction && (
                                    <div className="fv-plugins-message-container text-danger w-100">
                                        <span role="alert">{formData.errors.typeDeduction}</span>
                                    </div>
                                )}
                            </div>
                            <div className="d-flex flex-column flex-xl-row flex-row-fluid mt-5">
                                <div className="w-100 d-flex">
                                    <div className="w-100 pe-5">
                                        {(typeof typeDeduction?.typePerceptionDeductionId === 'number') && (<div className='container'>
                                            <div className='row'>
                                                {([1, 2, 3].includes(typeDeduction?.typePerceptionDeductionId))
                                                    && (
                                                        <div className='col'>
                                                            <h4>Valor</h4>
                                                            <p>{typeDeduction?.typePerceptionDeductionId === 2 ? `${Math.round(typeDeduction.value * 100)}%` : typeDeduction.value.toFixed(2)}</p>
                                                        </div>
                                                    )}
                                                {([3, 4].includes(typeDeduction?.typePerceptionDeductionId)) &&
                                                    (
                                                        <div className='col'>
                                                            <NumericComponent
                                                                label='Cantidad'
                                                                formData={formData}
                                                                name="amount"
                                                            />
                                                            {formData.errors.amount && formData.touched.amount && (
                                                                <div className="text-danger">{formData.errors.amount}</div>
                                                            )}
                                                        </div>
                                                    )}
                                            </div>
                                            <div className='row'>
                                                <div className='col mt-5'>
                                                    <h4>Valor Total</h4>
                                                    <p>{typeof value === 'number' ? value.toFixed(2) : '0.00'}</p>
                                                    {value > 999999 && <div className="text-danger">El valor total no puede ser mayor a 999,999</div>}
                                                </div>
                                            </div>
                                        </div>)}
                                    </div>
                                </div>
                            </div>

                            <div className="d-flex justify-content-end mt-10">
                                <button
                                    type="reset"
                                    className="btn btn-light me-3"
                                    data-kt-users-modal-action="cancel"
                                    onClick={handleClose}
                                >
                                    Cancelar
                                </button>
                                <button
                                    type="submit"
                                    className="btn btn-primary"
                                    data-kt-users-modal-action="submit"
                                >
                                    {!loading && <span className="indicator-label">Guardar</span>}
                                    {loading && (
                                        <span
                                            className="indicator-progress"
                                            style={{ display: "block" }}
                                        >
                                            Espere por favor...
                                            <span className="spinner-border spinner-border-sm align-middle ms-2"></span>
                                        </span>
                                    )}
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            </Modal>
        </div>
    );
};

export default Deductions;